import type { ComponentProps, RefObject } from 'react';
import type { To } from 'react-router';
import { merge } from 'lodash-es';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import useMeasure from 'react-use-measure';

import type { DynamicIconName } from '../../assets/DynamicIcon/DynamicIcon';
import type { TabProps } from '../../components/Tabs/Tabs';
import type { GutterProp } from '../../utilities/shared/Gutter';
import type { MaxWidthTypes } from '../../utilities/shared/MaxWidth';
import type { MinWidthTypes } from '../../utilities/shared/MinWidth';
import type { SpacingScale } from '../../utilities/shared/sizes';
import type { WidthTypes } from '../../utilities/shared/Width';
import type { Breakpoint } from '../../utilities/useViewport';
import type {
  ShellPropContentMode,
  ShellPropLayoutMode,
  ShellPropSize,
  ShellPropType,
  ShellPropWidthMode,
} from './utils';
import { Divider } from '../../assets/Divider/Divider';
import { DynamicIcon } from '../../assets/DynamicIcon/DynamicIcon';
import { Icon } from '../../assets/Icon/Icon';
import { backgrounds } from '../../common/backgrounds';
import { sizing } from '../../common/sizing';
import { zIndexes } from '../../common/z_indexes';
import { Badge } from '../../components/Badge/Badge';
import { BadgeGroup } from '../../components/Badge/BadgeGroup';
import { Tabs } from '../../components/Tabs/Tabs';
import { ControlGroup } from '../../controls/ControlGroup/ControlGroup';
import { UtilityControl } from '../../controls/UtilityControl/UtilityControl';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { BodySansSizes } from '../../text/Body';
import { SmallSansSizes } from '../../text/Small';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { maxWidthCSS } from '../../utilities/shared/MaxWidth';
import { minWidthCSS } from '../../utilities/shared/MinWidth';
import { spacingCSS } from '../../utilities/shared/Spacing';
import { widthCSS } from '../../utilities/shared/Width';
import {
  HorizontalScrollShadow,
  useHorizontalShadowOnScroll,
  useVerticalShadowOnScroll,
  VerticalScrollShadow,
} from '../../utilities/useShadowOnScroll';
import { StuckProvider } from '../../utilities/useStuck';
import { useViewport } from '../../utilities/useViewport';
import {
  ShellContentModeProvider,
  ShellLayerScrollProvider,
  ShellLayoutModeProvider,
  ShellMaxWidthProvider,
  ShellMinWidthProvider,
  ShellSizeProvider,
  ShellTypeProvider,
  ShellWidthModeProvider,
  ShellWidthProvider,
  useShellContentMode,
  useShellMaxWidth,
  useShellMinWidth,
  useShellSize,
  useShellType,
  useShellWidth,
  useShellWidthMode,
} from './utils';

const ShellHeaderIcon = styled(DynamicIcon, {
  display: 'flex',

  variants: {
    hasSize: {
      'x-small': {
        widthAll: '$12',
        heightAll: '$12',
      },
      small: {
        widthAll: '$14',
        heightAll: '$14',
      },
      medium: {
        widthAll: '$16',
        heightAll: '$16',
      },
    },
  },
});
ShellHeaderIcon.displayName = 'ShellHeaderIcon';

const ShellHeaderToolbarParentIcon = styled(DynamicIcon, {
  display: 'flex',

  variants: {
    hasSize: {
      'x-small': {
        widthAll: '$12',
        heightAll: '$12',
      },
      small: {
        widthAll: '$14',
        heightAll: '$14',
      },
      medium: {
        widthAll: '$16',
        heightAll: '$16',
      },
    },
  },
});
ShellHeaderToolbarParentIcon.displayName = 'ShellHeaderToolbarParentIcon';

const ShellHeaderToolbarParentLabel = styled(Text);
ShellHeaderToolbarParentLabel.displayName = 'ShellHeaderToolbarParentLabel';

const ShellHeaderToolbarParentChevron = styled(Icon, {
  widthAll: '$10',
  heightAll: '$10',
});
ShellHeaderToolbarParentChevron.displayName = 'ShellHeaderToolbarParentChevron';

const ShellHeaderToolbarHeading = styled(Text, {
  maxWidth: '100%',
  minWidth: 0,
  truncate: true,
  color: colors.headingNeutralLight,
  fontWeight: '$bold',

  [darkThemeSelector]: {
    color: colors.headingNeutralDark,
  },

  variants: {
    size: {
      'x-small': {
        ...SmallSansSizes,
      },
      small: {
        ...BodySansSizes,
      },
      medium: {
        ...BodySansSizes,
      },
    },
  },
});
ShellHeaderToolbarHeading.displayName = 'ShellHeaderToolbarHeading';

const ShellHeaderToolbarScroll = styled('div', HorizontalScrollShadow, {
  minWidth: 0,
  display: 'flex',
  flex: 1,
  flexDirection: 'row',
  alignItems: 'center',
  overflowX: 'auto',
  overflowY: 'hidden',

  variants: {
    hasEnd: {
      true: {},
      false: {},
    },
    size: {
      'x-small': {
        '@notDesktop': {
          gap: '$6',
        },

        '@desktop': {
          gap: '$4',
          minHeight: '$32',
        },
      },
      small: {
        '@notDesktop': {
          gap: '$6',
        },

        '@desktop': {
          gap: '$4',
          minHeight: '$32',
        },
      },
      medium: {
        '@notDesktop': {
          gap: '$8',
        },

        '@desktop': {
          gap: '$6',
        },
      },
    },
  },
});
ShellHeaderToolbarScroll.displayName = 'ShellHeaderToolbarScroll';

const ShellHeaderToolbarEnd = styled('div', {
  minWidth: 0,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-end',

  variants: {
    hasStart: {
      true: {},
      false: {},
    },
    size: {
      'x-small': {
        '@notDesktop': {
          gap: '$6',
        },

        '@desktop': {
          gap: '$4',
        },
      },
      small: {
        '@notDesktop': {
          gap: '$6',
        },

        '@desktop': {
          gap: '$4',
        },
      },
      medium: {
        '@notDesktop': {
          gap: '$8',
        },

        '@desktop': {
          gap: '$6',
        },
      },
    },
  },
});
ShellHeaderToolbarEnd.displayName = 'ShellHeaderToolbarEnd';

const ShellHeaderRowDivider = styled(Divider, {
  padding: '$12 0',
});
ShellHeaderRowDivider.displayName = 'ShellHeaderRowDivider';

const ShellHeaderRow = styled('div', {
  position: 'relative',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',

  '&::after': {
    content: '',
    position: 'absolute',
    bottom: '-0.5px',
    display: 'block',
    height: '$1',
    marginTop: '-0.5px',
    backgroundColor: colors.strokeNeutralLight,

    [darkThemeSelector]: {
      backgroundColor: colors.strokeNeutralDark,
    },
  },

  variants: {
    size: {
      'x-small': {
        '@notDesktop': {
          minHeight: '$40',
        },

        '@desktop': {
          minHeight: '$32',
        },

        '&::after': {
          right: sizing.contentSides,
          left: sizing.contentSides,
        },
      },
      small: {
        '@notDesktop': {
          minHeight: '$48',
        },

        '@desktop': {
          minHeight: '$40',
        },

        '&::after': {
          right: sizing.contentSides,
          left: sizing.contentSides,
        },
      },
      medium: {
        '@notDesktop': {
          minHeight: '$52',
        },

        '@desktop': {
          minHeight: '$44',
        },

        '&::after': {
          right: sizing.sides,
          left: sizing.sides,
        },
      },
    },
  },
});
ShellHeaderRow.displayName = 'ShellHeaderRow';

const ShellHeaderToolbar = styled(ShellHeaderRow);
ShellHeaderToolbar.displayName = 'ShellHeaderToolbar';

const ShellHeaderToolbarBlock = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',

  variants: {
    hasScroll: {
      true: {
        flex: 1,
        minWidth: 0,
      },
      false: {
        flex: '0 1 auto',
        minWidth: 0,

        '@notDesktop': {
          flex: '1 1 0%',
          minHeight: '100%',

          '&:first-child': {
            flex: '0 1 auto',
          },
        },

        '&:last-child': {
          flex: '0 0 auto',
          justifyContent: 'flex-end',
        },
      },
    },
    size: {
      'x-small': {},
      small: {},
      medium: {},
    },
  },

  compoundVariants: [
    {
      hasScroll: false,
      size: 'x-small',
      css: {
        gap: '$6',
        padding: '$6 $8',

        [`&:first-child`]: {
          paddingLeft: sizing.contentSides,
        },

        [`&:last-child`]: {
          paddingRight: sizing.contentSides,
        },
      },
    },
    {
      hasScroll: false,
      size: 'medium',
      css: {
        gap: '$8',
        padding: '$8 $10',

        [`&:first-child`]: {
          paddingLeft: sizing.sides,
        },

        [`&:last-child`]: {
          paddingRight: sizing.sides,
        },
      },
    },
    {
      hasScroll: true,
      size: 'x-small',
      css: {
        [`${ShellHeaderToolbarScroll}`]: {
          padding: '$6 $8',
        },

        [`&:first-child ${ShellHeaderToolbarScroll}`]: {
          paddingLeft: sizing.contentSides,
        },

        [`&:last-child ${ShellHeaderToolbarScroll}`]: {
          paddingRight: sizing.contentSides,
        },
      },
    },
    {
      hasScroll: true,
      size: 'medium',
      css: {
        [`${ShellHeaderToolbarScroll}`]: {
          padding: '$8 $10',
        },

        [`&:first-child ${ShellHeaderToolbarScroll}`]: {
          paddingLeft: sizing.sides,
        },

        [`&:last-child ${ShellHeaderToolbarScroll}`]: {
          paddingRight: sizing.sides,
        },
      },
    },
  ],
});
ShellHeaderToolbarBlock.displayName = 'ShellHeaderToolbarBlock';

const ShellHeaderToolbarHeader = styled(AlignStack, {
  width: 'auto',
  minWidth: 0,
  flexShrink: 1,
});
ShellHeaderToolbarHeader.displayName = 'ShellHeaderToolbarHeader';

const ShellHeaderToolbarTabsDivider = styled(Divider, {
  padding: '$4',
});
ShellHeaderToolbarTabsDivider.displayName = 'ShellHeaderToolbarTabsDivider';

const ShellHeaderToolbarTabs = styled('div', {
  minWidth: 0,
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  overflow: 'hidden',

  '@notDesktop': {
    width: '100%',
  },
});
ShellHeaderToolbarTabs.displayName = 'ShellHeaderToolbarTabs';

const ShellHeaderViews = styled(ShellHeaderRow);
ShellHeaderViews.displayName = 'ShellHeaderViews';

const ShellHeaderWidth = styled('div', {
  display: 'flex',
  flexDirection: 'column',
});
ShellHeaderWidth.displayName = 'ShellHeaderWidth';

const ShellHeaderContainer = styled('div', {
  gridArea: 'shell-header',
  position: 'relative',
  zIndex: 2,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',

  variants: {
    size: {
      'x-small': {},
      small: {},
      medium: {},
    },
    widthMode: {
      centered: {
        alignItems: 'center',
      },
      full: {},
    },
  },
});
ShellHeaderContainer.displayName = 'ShellHeaderContainer';

export type ShellHeaderProps = {
  /**
   * Provide any actions for your pane.
   */
  actions?: React.ReactNode;
  /**
   * Show relevant badges alongside the `heading`.
   */
  badges?: React.ReactNode;
  /**
   * Provide a label for the close button.
   */
  closeButtonAriaLabel?: string;
  /**
   * Show the total count of items.
   */
  count?: React.ReactNode;
  /**
   * Provide a heading for your pane.
   */
  heading?: React.ReactNode;
  /**
   * Provide an icon for your pane.
   */
  icon?: DynamicIconName;
  /**
   * Provide a handler and opt into adding a close icon to the right.
   */
  onClose?: React.ReactEventHandler;
  /**
   * Provide a parent for your pane.
   */
  parent?: {
    /**
     * Provide an icon for the parent.
     */
    icon?: DynamicIconName;
    /**
     * Provide a label for the parent.
     */
    label?: React.ReactNode;
    // Routing
    as?: React.ElementType;
    to?: To;
  };
  /**
   * Provide any subtabs in addition to tabs.
   */
  secondaryTabs?: TabProps[];
  /**
   * Allows for a select interface to switch between objects.
   */
  switcher?: React.ReactNode;
  /**
   * Provide any tabs you want to display.
   */
  tabs?: TabProps[];
  /**
   * If only one tab is visible, don't display it.
   */
  hideSingleTab?: boolean;
  /**
   * Provide the views and filters utilizing the `views` prop.
   */
  views?: React.ReactNode;
  /**
   * Provide a layout mode for your shell.
   * Note: Automatically provided from the `Shell` component, you likely don't need to provide this.
   */
  layoutMode?: ShellPropLayoutMode;
  /**
   * Provide a size for your shell.
   * Note: Automatically provided from the `Shell` component, you likely don't need to provide this.
   */
  size?: ShellPropSize;
  // Deprecated
  oldTabs?: React.ReactNode;
  oldSubtabs?: React.ReactNode;
};

const shellHeaderControlSize = (size: ShellPropSize | undefined, breakpoint: Breakpoint) => {
  if (size === 'x-small') return 'x-small';
  if (breakpoint === 'desktop') return 'small';
  return 'medium';
};

function ShellHeaderActions({
  actions,
  onClose,
  closeButtonAriaLabel,
  size,
}: Omit<ShellHeaderProps, 'back' | 'home' | 'crumbs' | 'switcher' | 'tabs' | 'subtabs' | 'views'>) {
  const { breakpoint } = useViewport();
  return (
    <>
      {actions && (
        <ControlGroup size={shellHeaderControlSize(size, breakpoint)} relation="separate">
          {actions}
        </ControlGroup>
      )}
      {onClose && (
        <UtilityControl
          onClick={onClose}
          label={closeButtonAriaLabel}
          variant="neutral"
          shortcut={['ESC']}
        />
      )}
    </>
  );
}

export function ShellHeader({
  actions,
  badges,
  closeButtonAriaLabel = 'Close',
  count,
  heading,
  icon,
  layoutMode,
  onClose,
  parent,
  size,
  secondaryTabs,
  switcher,
  tabs,
  hideSingleTab = true,
  views,
  // Deprecated
  oldTabs,
  oldSubtabs,
  ...remaining
}: ShellHeaderProps) {
  const { breakpoint } = useViewport();
  const [containerRef, { width: containerWidth }] = useMeasure();
  const shellSize = useShellSize(size, 'medium');
  const shellType = useShellType();
  const shellWidth = useShellWidth();
  const shellMaxWidth = useShellMaxWidth();
  const shellMinWidth = useShellMinWidth();
  const shellWidthMode = useShellWidthMode();

  const toolbarTabsRef = useRef<HTMLDivElement>(null);
  const {
    getBoxShadow: getToolbarTabsShadow,
    onScrollHandler: onToolbarTabsScrollHandler,
    handleTargetChange: handleToolbarTabsTargetChange,
  } = useHorizontalShadowOnScroll('both');
  const handleResizeToolbarTabs = useCallback(() => {
    if (toolbarTabsRef.current) {
      handleToolbarTabsTargetChange(toolbarTabsRef.current);
    }
  }, [handleToolbarTabsTargetChange]);
  useEffect(() => {
    handleResizeToolbarTabs();
    window.addEventListener('resize', handleResizeToolbarTabs);
    return () => {
      window.removeEventListener('resize', handleResizeToolbarTabs);
    };
  }, [handleResizeToolbarTabs]);

  const hasToolbarStart = Boolean(switcher || icon || heading || count);
  const hasToolbarTabs = Boolean(
    (tabs && (tabs.length > 1 || (tabs.length > 0 && !hideSingleTab))) ||
      secondaryTabs ||
      oldTabs ||
      oldSubtabs,
  );

  const hasToolbarEnd = Boolean(actions || onClose);
  const hasToolbar = Boolean(
    hasToolbarStart || (shellType === 'pane' && (hasToolbarTabs || hasToolbarEnd)),
  );

  const hasViews = Boolean(views);

  const hasHeader = hasToolbar || (shellType !== 'pane' && hasToolbarTabs) || hasViews;

  const isNarrow =
    breakpoint !== 'desktop' ||
    // On initial paint containerWidth will be 0
    (containerWidth !== 0 && containerWidth < 720);
  const isMobile = breakpoint === 'mobile';

  // When narrow, tabs move to a separate row below. The first toolbar only has
  // start (heading, icon, etc.) and end (actions) content — hide it if empty.
  const hasFirstToolbarContent =
    isNarrow || isMobile ? hasToolbarStart || hasToolbarEnd : hasToolbar;

  return hasHeader ? (
    <ShellHeaderContainer size={shellSize} widthMode={shellWidthMode} {...remaining}>
      <ShellHeaderWidth
        ref={containerRef}
        css={merge(
          shellWidth ? widthCSS(shellWidth) : undefined,
          shellMaxWidth ? maxWidthCSS(shellMaxWidth) : undefined,
          shellMinWidth ? minWidthCSS(shellMinWidth) : undefined,
        )}
      >
        {hasFirstToolbarContent && (
          <ShellHeaderToolbar size={shellSize}>
            {hasToolbarStart && (
              <ShellHeaderToolbarBlock hasScroll={false} size={shellSize}>
                {!isNarrow && parent && (
                  <AlignStack
                    as={parent.as}
                    to={parent.to}
                    gap={6}
                    preset={shellSize === 'x-small' ? 'small' : 'body'}
                    width="min-content"
                    start={
                      parent.icon && (
                        <ShellHeaderToolbarParentIcon
                          hasSize={shellSize}
                          icon={parent.icon}
                          variant="neutral"
                        />
                      )
                    }
                    end={<ShellHeaderToolbarParentChevron icon="chevron-right" variant="neutral" />}
                  >
                    <ShellHeaderToolbarParentLabel
                      context="body"
                      variant="neutral"
                      weight="bold"
                      whitespace="no-wrap"
                    >
                      {parent.label}
                    </ShellHeaderToolbarParentLabel>
                  </AlignStack>
                )}
                <ShellHeaderToolbarHeader
                  direction="row"
                  align="center"
                  gap={{
                    start: shellSize === 'medium' ? 6 : 4,
                    end: shellSize === 'medium' ? 6 : 4,
                  }}
                  preset={shellSize === 'medium' ? 'body' : 'small'}
                  start={
                    icon && <ShellHeaderIcon hasSize={shellSize} icon={icon} variant="neutral" />
                  }
                  end={
                    badges || count ? (
                      <BadgeGroup
                        relation="separate"
                        size={shellSize === 'x-small' ? 'small' : 'medium'}
                      >
                        {count && (
                          <Badge
                            ends="card"
                            size={shellSize === 'x-small' ? 'x-small' : 'small'}
                            variant="neutral"
                          >
                            {count}
                          </Badge>
                        )}
                        {badges}
                      </BadgeGroup>
                    ) : null
                  }
                >
                  {switcher ||
                    (heading && (
                      <ShellHeaderToolbarHeading size={shellSize}>
                        {heading}
                      </ShellHeaderToolbarHeading>
                    ))}
                </ShellHeaderToolbarHeader>
              </ShellHeaderToolbarBlock>
            )}
            {!isNarrow && hasToolbarStart && hasToolbarTabs && (
              <ShellHeaderRowDivider appearance="thick" direction="column" variant="neutral" />
            )}
            {!isNarrow && hasToolbarTabs && (
              <ShellHeaderToolbarBlock hasScroll size={shellSize}>
                <ShellHeaderToolbarTabs>
                  <ShellHeaderToolbarScroll
                    size={shellSize}
                    hasEnd={hasToolbarEnd}
                    onScroll={onToolbarTabsScrollHandler}
                    ref={toolbarTabsRef}
                    {...getToolbarTabsShadow}
                  >
                    {(tabs || secondaryTabs) && (
                      <Tabs
                        tabs={tabs}
                        secondaryTabs={secondaryTabs}
                        hideSingleTab={hideSingleTab}
                      />
                    )}
                    {oldTabs && <Tabs>{oldTabs}</Tabs>}
                    {oldTabs && oldSubtabs && (
                      <ShellHeaderToolbarTabsDivider
                        appearance="thick"
                        direction="column"
                        variant="neutral"
                      />
                    )}
                    {oldSubtabs && <Tabs>{oldSubtabs}</Tabs>}
                  </ShellHeaderToolbarScroll>
                </ShellHeaderToolbarTabs>
              </ShellHeaderToolbarBlock>
            )}
            {hasToolbarEnd && (
              <ShellHeaderToolbarBlock hasScroll={false} size={shellSize}>
                <ShellHeaderToolbarEnd size={shellSize} hasStart={hasToolbarStart}>
                  <ShellHeaderActions
                    actions={!isMobile || !hasToolbarTabs ? actions : undefined}
                    onClose={onClose}
                    closeButtonAriaLabel={closeButtonAriaLabel}
                    size={shellSize}
                  />
                </ShellHeaderToolbarEnd>
              </ShellHeaderToolbarBlock>
            )}
          </ShellHeaderToolbar>
        )}
        {(isNarrow || isMobile) && hasToolbarTabs && (
          <ShellHeaderToolbar size={shellSize}>
            <ShellHeaderToolbarBlock hasScroll size={shellSize}>
              <ShellHeaderToolbarTabs>
                <ShellHeaderToolbarScroll
                  size={shellSize}
                  hasEnd={hasToolbarEnd}
                  onScroll={onToolbarTabsScrollHandler}
                  ref={toolbarTabsRef}
                  {...getToolbarTabsShadow}
                >
                  {(tabs || secondaryTabs) && <Tabs tabs={tabs} secondaryTabs={secondaryTabs} />}
                  {oldTabs && <Tabs>{oldTabs}</Tabs>}
                  {oldTabs && oldSubtabs && (
                    <ShellHeaderToolbarTabsDivider
                      appearance="thick"
                      direction="column"
                      variant="neutral"
                    />
                  )}
                  {oldSubtabs && <Tabs>{oldSubtabs}</Tabs>}
                </ShellHeaderToolbarScroll>
              </ShellHeaderToolbarTabs>
            </ShellHeaderToolbarBlock>
            {isMobile && hasToolbarEnd && (
              <ShellHeaderToolbarBlock hasScroll={false} size={shellSize}>
                <ShellHeaderToolbarEnd size={shellSize} hasStart={hasToolbarStart}>
                  <ShellHeaderActions actions={actions} size={shellSize} />
                </ShellHeaderToolbarEnd>
              </ShellHeaderToolbarBlock>
            )}
          </ShellHeaderToolbar>
        )}
        {hasViews && <ShellHeaderViews>{views}</ShellHeaderViews>}
      </ShellHeaderWidth>
    </ShellHeaderContainer>
  ) : null;
}

const ShellContentStuckInnerPosition = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: 'fit-content',

  variants: {
    stuck: {
      true: {
        background: colors.bgApplicationLight,
        boxShadow: shadows.stuckLight,

        [darkThemeSelector]: {
          background: colors.bgApplicationDark,
          boxShadow: shadows.stuckDark,
        },
      },
      false: {
        '&::after': {
          content: '',
          position: 'absolute',
          right: sizing.sides,
          bottom: '-0.5px',
          left: sizing.sides,
          display: 'block',
          height: '$1',
          background: colors.strokeNeutralLight,

          [darkThemeSelector]: {
            background: colors.strokeNeutralDark,
          },
        },
      },
    },
  },
});
ShellContentStuckInnerPosition.displayName = 'ShellContentStuckInnerPosition';

const ShellContentStuckInnerContainer = styled('div', {
  gridArea: 'stuck',
  display: 'flex',
  width: '100%',
});
ShellContentStuckInnerContainer.displayName = 'ShellContentStuckInnerContainer';

type ShellContentGlanceProps = {
  /**
   * Provide the content for the glance.
   */
  children: React.ReactNode;
  /**
   * Boolean to determine if the element is stuck or not.
   */
  stuck?: boolean;
  /**
   * Ref to the element.
   */
  ref?: React.Ref<HTMLDivElement | null>;
};

export function ShellContentStuckInner({ children, stuck, ref }: ShellContentGlanceProps) {
  return (
    <StuckProvider value={stuck}>
      <ShellContentStuckInnerContainer ref={ref}>
        <ShellContentStuckInnerPosition stuck={stuck}>{children}</ShellContentStuckInnerPosition>
      </ShellContentStuckInnerContainer>
    </StuckProvider>
  );
}

const useShellContentStuck = () => {
  const [isOutOfView, setIsOutOfView] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Make the sticky header visible when 70% of the "real" header is out of view.
  useLayoutEffect(() => {
    const callback = (entries: IntersectionObserverEntry[]) => {
      // ensures that the element is rendered with content without display: none; (a result of AppSkeleton)
      if (ref.current?.clientHeight) {
        setIsOutOfView(!entries[0].isIntersecting);
      }
    };

    const observer = new IntersectionObserver(callback, {
      root: null,
      threshold: 0.3,
      rootMargin: '0px 0px 0px 0px',
    });

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      observer?.disconnect();
    };
  }, []);

  return { ref, isOutOfView };
};

const ShellContentStuckZero = styled('div', {
  gridArea: 'stuck',
  position: 'sticky',
  zIndex: zIndexes.paneStuck,
  top: 0,
  height: 0,
  opacity: 0,
  transition: 'opacity 200ms ease-in-out',

  variants: {
    visible: {
      true: {
        display: 'flex',
        opacity: 1,
      },
      false: {
        opacity: 0,
        pointerEvents: 'none',
      },
    },
  },
});
ShellContentStuckZero.displayName = 'ShellContentStuckZero';

export function ShellContentStuck({
  spacing,
  ...props
}: { spacing: SpacingScale } & ShellContentGlanceProps) {
  const { ref, isOutOfView } = useShellContentStuck();

  return (
    <>
      <ShellContentStuckZero
        visible={isOutOfView}
        aria-hidden="true"
        style={{ marginTop: `-${spacing}px` }}
      >
        <ShellContentStuckInner {...props} stuck />
      </ShellContentStuckZero>
      <ShellContentStuckInner {...props} stuck={false} ref={ref} />
    </>
  );
}

const ShellContentDiagram = styled('div', {
  position: 'relative',
  display: 'flex',
});
ShellContentDiagram.displayName = 'ShellContentDiagram';

const ShellContentGlance = styled('div', {
  position: 'relative',
  display: 'flex',
});
ShellContentGlance.displayName = 'ShellContentGlance';

const ShellContentControls = styled('div', {
  position: 'relative',
  display: 'flex',
});
ShellContentControls.displayName = 'ShellContentControls';

export const ShellContentSpacing = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',

  variants: {
    contentMode: {
      fit: {
        height: 'fit-content',
      },
      stretch: {
        height: '100%',
      },
    },
    gutter: {
      all: {},
      vertical: {},
      horizontal: {},
      top: {},
      right: {},
      bottom: {},
      left: {},
      none: {
        padding: 0,
      },
    },
    size: {
      'x-small': {},
      small: {},
      medium: {},
    },
  },

  compoundVariants: [
    {
      size: 'x-small',
      gutter: 'all',
      css: {
        padding: sizing.contentSquish,
      },
    },
    {
      size: 'x-small',
      gutter: 'vertical',
      css: {
        padding: sizing.contentEndsOnly,
      },
    },
    {
      size: 'x-small',
      gutter: 'horizontal',
      css: {
        padding: sizing.contentSidesOnly,
      },
    },
    {
      size: 'x-small',
      gutter: 'top',
      css: {
        paddingTop: sizing.contentEnds,
      },
    },
    {
      size: 'x-small',
      gutter: 'right',
      css: {
        paddingRight: sizing.contentSides,
      },
    },
    {
      size: 'x-small',
      gutter: 'bottom',
      css: {
        paddingBottom: sizing.contentEnds,
      },
    },
    {
      size: 'x-small',
      gutter: 'left',
      css: {
        paddingLeft: sizing.contentSides,
      },
    },
    {
      size: 'medium',
      gutter: 'all',
      css: {
        padding: sizing.squish,
      },
    },
    {
      size: 'medium',
      gutter: 'vertical',
      css: {
        padding: sizing.endsOnly,
      },
    },
    {
      size: 'medium',
      gutter: 'horizontal',
      css: {
        padding: sizing.sidesOnly,
      },
    },
    {
      size: 'medium',
      gutter: 'top',
      css: {
        paddingTop: sizing.ends,
      },
    },
    {
      size: 'medium',
      gutter: 'right',
      css: {
        paddingRight: sizing.sides,
      },
    },
    {
      size: 'medium',
      gutter: 'bottom',
      css: {
        paddingBottom: sizing.ends,
      },
    },
    {
      size: 'medium',
      gutter: 'left',
      css: {
        paddingLeft: sizing.sides,
      },
    },
  ],
});
ShellContentSpacing.displayName = 'ShellContentSpacing';

const ShellContentScroll = styled(VerticalScrollShadow, {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  height: '100%',

  variants: {
    contentMode: {
      fit: {
        height: 'fit-content',
      },
      stretch: {
        height: '100%',
      },
    },
    widthMode: {
      centered: {
        alignItems: 'center',
      },
      full: {},
    },
  },
});
ShellContentScroll.displayName = 'ShellContentScroll';

const ShellContentStatic = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  height: 'fit-content',

  variants: {
    widthMode: {
      centered: {
        alignSelf: 'center',
      },
      full: {},
    },
  },
});
ShellContentStatic.displayName = 'ShellContentStatic';

const ShellContentPosition = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  overflow: 'hidden',

  variants: {
    contentMode: {
      fit: {
        height: 'fit-content',
      },
      stretch: {
        height: '100%',
      },
    },
    variant: {
      scroll: {},
      static: {
        flex: '1 0 auto',
        heightAll: 'min-content',

        '@notDesktop': {
          width: '100%',
        },
      },
    },
  },
});
ShellContentPosition.displayName = 'ShellContentPosition';

const ShellContentDivider = styled(Divider, {
  padding: `0 $${sizing.primary}`,
});
ShellContentDivider.displayName = 'ShellContentDivider';

const ShellContentContainer = styled('div', {
  gridArea: 'shell-content',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  overflow: 'hidden',

  variants: {
    background: {
      checkered: {
        background: backgrounds.checkeredLight,
        backgroundColor: colors.bgApplicationLight,

        [darkThemeSelector]: {
          background: backgrounds.checkeredDark,
          backgroundColor: colors.bgApplicationDark,
        },
      },
      dotted: {
        background: backgrounds.dottedLight,
        backgroundColor: colors.bgApplicationLight,

        [darkThemeSelector]: {
          background: backgrounds.dottedDark,
          backgroundColor: colors.bgApplicationDark,
        },
      },
      default: {},
    },
  },
});
ShellContentContainer.displayName = 'ShellContentContainer';

export type ShellContentProps = {
  /**
   * Set whether the background should be checkered or not.
   */
  background?: 'default' | 'checkered' | 'dotted';
  /**
   * Pass in any content as `children`.
   */
  children?: React.ReactNode;
  /**
   * Set the content mode for the content.
   */
  contentMode?: ShellPropContentMode;
  /**
   * Provide diagram for the content, which goes before controls.
   */
  diagram?: React.ReactNode;
  /**
   * Provide controls for the content.
   */
  controls?: React.ReactNode;
  /**
   * Provide glance for the content, which goes after controls.
   */
  glance?: React.ReactNode;
  /**
   * Set whether there should be a gutter or not around the children.
   */
  gutter?: GutterProp;
  /**
   * Set the size of the shell.
   */
  size?: ShellPropSize;
  /**
   * Set the spacing between the children.
   */
  spacing?: SpacingScale;
  /**
   * Ref to the scroll container.
   */
  scrollRef?: RefObject<HTMLDivElement | null>;
} & ComponentProps<typeof ShellContentContainer>;

export function ShellContent({
  children,
  background,
  diagram,
  controls,
  glance,
  gutter = 'all',
  size,
  spacing = 16,
  scrollRef,
  ref,
  contentMode,
  ...remaining
}: ShellContentProps) {
  const shellContentMode = useShellContentMode(contentMode);
  const shellSize = useShellSize(size);
  const shellWidth = useShellWidth();
  const shellMaxWidth = useShellMaxWidth();
  const shellMinWidth = useShellMinWidth();
  const shellWidthMode = useShellWidthMode();
  const innerScrollRef = useRef<HTMLDivElement>(null);
  const { getBoxShadow, onScrollHandler, handleTargetChange } = useVerticalShadowOnScroll('both');

  const handleResize = useCallback(() => {
    if (innerScrollRef.current) {
      handleTargetChange(innerScrollRef.current);
    }
    if (scrollRef?.current) {
      handleTargetChange(scrollRef.current);
    }
  }, [handleTargetChange, scrollRef]);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  const scrollProps = {
    ref: scrollRef ?? innerScrollRef,
    onScroll: onScrollHandler,
    ...getBoxShadow,
  };

  // Get the actual scroll container element
  const scrollContainer = scrollRef?.current || innerScrollRef.current;

  return (
    <ShellContentContainer ref={ref} background={background} {...remaining}>
      {controls || glance ? (
        <ShellContentPosition variant="static">
          <ShellContentStatic
            widthMode={shellWidthMode}
            css={merge(
              shellWidth ? widthCSS(shellWidth) : undefined,
              shellMaxWidth ? maxWidthCSS(shellMaxWidth) : undefined,
              shellMinWidth ? minWidthCSS(shellMinWidth) : undefined,
            )}
          >
            {diagram && (
              <>
                <ShellContentDivider />
                <ShellContentDiagram>{diagram}</ShellContentDiagram>
              </>
            )}
            {controls && (
              <>
                <ShellContentDivider />
                <ShellContentControls>{controls}</ShellContentControls>
              </>
            )}
            {glance && (
              <>
                <ShellContentDivider />
                <ShellContentGlance>{glance}</ShellContentGlance>
              </>
            )}
            <ShellContentDivider />
          </ShellContentStatic>
        </ShellContentPosition>
      ) : null}
      <ShellContentPosition contentMode={shellContentMode}>
        <ShellContentScroll
          contentMode={shellContentMode}
          widthMode={shellWidthMode}
          {...scrollProps}
        >
          <ShellLayerScrollProvider scrollContainer={scrollContainer}>
            <ShellContentSpacing
              size={shellSize}
              gutter={gutter}
              contentMode={shellContentMode}
              css={merge(
                spacingCSS(spacing),
                shellWidth ? widthCSS(shellWidth) : undefined,
                shellMaxWidth ? maxWidthCSS(shellMaxWidth) : undefined,
                shellMinWidth ? minWidthCSS(shellMinWidth) : undefined,
              )}
            >
              {children}
            </ShellContentSpacing>
          </ShellLayerScrollProvider>
        </ShellContentScroll>
      </ShellContentPosition>
    </ShellContentContainer>
  );
}

const ShellFooterEnds = styled(ControlGroup, {
  width: '100%',
});
ShellFooterEnds.displayName = 'ShellFooterEnds';

const ShellFooterStart = styled(ShellFooterEnds);
ShellFooterStart.displayName = 'ShellFooterStart';

const ShellFooterEnd = styled(ShellFooterEnds, {
  justifyContent: 'flex-end',
});
ShellFooterEnd.displayName = 'ShellFooterEnd';

export const ShellFooterWidth = styled('div', {
  position: 'relative',
  display: 'flex',
  flexShrink: 0,
  gap: '$12',
  width: '100%',
  overflow: 'auto',
  background: colors.bgApplicationLight,

  [darkThemeSelector]: {
    background: colors.bgApplicationDark,
  },

  '@mobile': {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },

  '@notMobile': {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  '&:before': {
    content: '',
    display: 'block',
    position: 'absolute',
    top: '-0.5px',
    right: sizing.sides,
    left: sizing.sides,
    height: '$1',
    background: colors.strokeNeutralLight,
    borderRadius: '$2',

    [darkThemeSelector]: {
      background: colors.strokeNeutralDark,
    },
  },

  variants: {
    size: {
      'x-small': {
        padding: `$6 ${sizing.contentSides}`,

        '@notDesktop': {
          minHeight: '$40',
        },

        '@desktop': {
          minHeight: '$32',
        },
      },
      small: {
        padding: `$8 ${sizing.contentSides}`,

        '@notDesktop': {
          minHeight: '$48',
        },

        '@desktop': {
          minHeight: '$40',
        },
      },
      medium: {
        padding: `$10 ${sizing.sides}`,

        '@notDesktop': {
          minHeight: '$52',
        },

        '@desktop': {
          minHeight: '$48',
        },
      },
    },
  },
});
ShellFooterWidth.displayName = 'ShellFooterWidth';

const ShellFooterContainer = styled('div', {
  gridArea: 'shell-footer',
  position: 'relative',
  display: 'flex',

  '@mobile': {
    flexDirection: 'row',
  },

  '@notMobile': {
    flexDirection: 'column',
  },

  variants: {
    widthMode: {
      centered: {
        alignItems: 'center',
      },
      full: {},
    },
  },
});
ShellFooterContainer.displayName = 'ShellFooterContainer';

export type ShellFooterProps = {
  /**
   * The end content for the footer, usually used for actions.
   */
  end?: React.ReactNode;
  /**
   * Optional ref to use for the footer.
   */
  ref?: RefObject<HTMLDivElement | null>;
  /**
   * The start content for the footer, usually used for additional context and helpers.
   */
  start?: React.ReactNode;
  /**
   * Set the size of the shell.
   */
  size?: ShellPropSize;
};

export function ShellFooter({ end, size, start, ref, ...remaining }: ShellFooterProps) {
  const shellSize = useShellSize(size, 'medium');
  const shellWidth = useShellWidth();
  const shellMaxWidth = useShellMaxWidth();
  const shellMinWidth = useShellMinWidth();
  const shellWidthMode = useShellWidthMode();

  return (
    <ShellFooterContainer ref={ref} widthMode={shellWidthMode} {...remaining}>
      <ShellFooterWidth
        size={shellSize}
        css={merge(
          shellWidth ? widthCSS(shellWidth) : undefined,
          shellMaxWidth ? maxWidthCSS(shellMaxWidth) : undefined,
          shellMinWidth ? minWidthCSS(shellMinWidth) : undefined,
        )}
      >
        {start && (
          <ShellFooterStart relation="separate" size={size === 'x-small' ? 'x-small' : 'medium'}>
            {start}
          </ShellFooterStart>
        )}
        {end && (
          <ShellFooterEnd relation="separate" size={size === 'x-small' ? 'x-small' : 'medium'}>
            {end}
          </ShellFooterEnd>
        )}
      </ShellFooterWidth>
    </ShellFooterContainer>
  );
}

export const ShellContainer = styled('div', {
  display: 'grid',
  gridTemplateAreas: '"shell-toolbar" "shell-header" "shell-content" "shell-footer"',
  width: '100%',
  height: '100%',
  overflow: 'hidden',

  '& form': {
    // This allows for the use of a `<form>` inside of any `Shell` components
    // without affecting the flexbox of the children.
    display: 'contents',
  },

  variants: {
    contentMode: {
      fit: {
        gridTemplateRows:
          'fit-content(100%) fit-content(100%) minmax(0, min-content) fit-content(100%)',
      },
      stretch: {
        gridTemplateRows: 'fit-content(100%) fit-content(100%) 1fr fit-content(100%)',
      },
    },
    widthMode: {
      centered: {},
      full: {},
    },
  },
});
ShellContainer.displayName = 'ShellContainer';

export type ShellProps = {
  children: React.ReactNode;
  contentMode?: ShellPropContentMode;
  layoutMode?: ShellPropLayoutMode;
  size?: ShellPropSize;
  width?: WidthTypes;
  maxWidth?: MaxWidthTypes;
  minWidth?: MinWidthTypes;
  widthMode?: ShellPropWidthMode;
  type?: ShellPropType;
  ref?: RefObject<HTMLDivElement | null>;
};

export function Shell({
  children,
  contentMode = 'stretch',
  layoutMode = 'compact',
  size = 'medium',
  width,
  maxWidth,
  minWidth = 0,
  widthMode = 'full',
  type = 'pane',
  ref,
  ...remaining
}: ShellProps) {
  return (
    <ShellSizeProvider value={size}>
      <ShellLayoutModeProvider value={layoutMode}>
        <ShellContentModeProvider value={contentMode}>
          <ShellWidthModeProvider value={widthMode}>
            <ShellWidthProvider value={width}>
              <ShellMaxWidthProvider value={maxWidth}>
                <ShellMinWidthProvider value={minWidth}>
                  <ShellTypeProvider value={type}>
                    <ShellContainer
                      ref={ref}
                      contentMode={contentMode}
                      widthMode={widthMode}
                      css={merge(
                        widthMode === 'full' ? widthCSS(width) : undefined,
                        widthMode === 'full' ? maxWidthCSS(maxWidth) : undefined,
                        widthMode === 'full' ? minWidthCSS(minWidth) : undefined,
                      )}
                      {...remaining}
                    >
                      {children}
                    </ShellContainer>
                  </ShellTypeProvider>
                </ShellMinWidthProvider>
              </ShellMaxWidthProvider>
            </ShellWidthProvider>
          </ShellWidthModeProvider>
        </ShellContentModeProvider>
      </ShellLayoutModeProvider>
    </ShellSizeProvider>
  );
}
